import React from 'react';
import { List, Tabs } from 'antd';
import VideoItem from 'components/List/VideoItem';
import ArticleItem from 'components/List/ArticleItem';
import WhitePaperItem from 'components/List/WhitePaperItem';
import GalleryItem from 'components/List/GalleryItem';
import styled from 'styled-components';

const { TabPane } = Tabs;

const TabsStyled = styled(Tabs)`
    .ant-tabs-tab {
        font-size: 20px !important;
    }
`;

interface Props {
    galleries: Gallery[];
    videos: Video[];
    articles: Article[];
    whitePapers: WhitePaper[];
    itemsPerTab?: number;
}

const RelativeContentTabs = ({
    galleries = [],
    videos = [],
    articles = [],
    whitePapers = [],
    itemsPerTab = 999
}: Props): React.JSX.Element => {
    const multimedia = [
        ...galleries.map((gallery: Gallery) => ({
            ...gallery,
            type: 'gallery'
        })),
        ...videos.map((video: Video) => ({
            ...video,
            type: 'video'
        }))
    ]
        .sort((a: Video | Gallery, b: Video | Gallery): number => {
            return new Date(b.dateDisplayed).getTime() - new Date(a.dateDisplayed).getTime();
        })
        .slice(0, itemsPerTab);
    const sortedArticles = articles.sort((a: Article, b: Article): number => {
        return new Date(b.dateDisplayed).getTime() - new Date(a.dateDisplayed).getTime();
    });

    const sortedWhitePapers = whitePapers.sort((a: WhitePaper, b: WhitePaper): number => {
        return new Date(b.dateDisplayed).getTime() - new Date(a.dateDisplayed).getTime();
    });

    const all = [
        ...multimedia,
        ...sortedArticles.map((article) => ({
            ...article,
            type: 'article'
        })),
        ...sortedWhitePapers.map((item) => ({
            ...item,
            type: 'white-paper'
        }))
    ]
        .sort(
            (
                a: WhitePaper | Article | Gallery | Video,
                b: WhitePaper | Article | Gallery | Video
            ): number => {
                return new Date(b.dateDisplayed).getTime() - new Date(a.dateDisplayed).getTime();
            }
        )
        .slice(0, itemsPerTab);
    return (
        <TabsStyled defaultActiveKey="all" size="large">
            <TabPane tab="Wszystko" key="all">
                <List
                    itemLayout="horizontal"
                    dataSource={all}
                    rowKey={({
                        seoSlug,
                        type
                    }: (Article | Video | Gallery | WhitePaper) & {
                        type: string;
                    }): string => `${type}-${seoSlug}`}
                    grid={{ column: 1 }}
                    renderItem={({
                        type,
                        ...restProps
                    }: (Article | Video | Gallery | WhitePaper) & {
                        type: string;
                    }) => {
                        if (type === 'video') {
                            const video = restProps as Video;

                            return (
                                <List.Item>
                                    <VideoItem $video={video} />
                                </List.Item>
                            );
                        }
                        if (type === 'gallery') {
                            const gallery = restProps as Gallery;
                            return (
                                <List.Item>
                                    <GalleryItem $gallery={gallery} />
                                </List.Item>
                            );
                        }
                        if (type === 'white-paper') {
                            const whitePaper = restProps as WhitePaper;
                            return (
                                <List.Item>
                                    <WhitePaperItem $whitePaper={whitePaper} />
                                </List.Item>
                            );
                        }

                        const article = restProps as Article;
                        return (
                            <List.Item>
                                <ArticleItem $article={article} />
                            </List.Item>
                        );
                    }}
                />
            </TabPane>
            <TabPane tab="Artykuły" key="articles">
                <List
                    itemLayout="horizontal"
                    rowKey={({ seoSlug }: Article): string => seoSlug}
                    grid={{ column: 1 }}
                    dataSource={sortedArticles}
                    renderItem={(item: Article) => (
                        <List.Item>
                            <ArticleItem $article={item} />
                        </List.Item>
                    )}
                />
            </TabPane>
            <TabPane tab="Multimedia" key="multimedia">
                <List
                    itemLayout="horizontal"
                    rowKey={({ seoSlug }: Video | Gallery): string => seoSlug}
                    grid={{ column: 1 }}
                    dataSource={multimedia}
                    renderItem={({
                        type,
                        ...restProps
                    }: (Video | Gallery) & {
                        type: string;
                    }) => {
                        if (type === 'video') {
                            const video = restProps as Video;
                            return (
                                <List.Item>
                                    <VideoItem $video={video} />
                                </List.Item>
                            );
                        }
                        const gallery = restProps as Gallery;
                        return (
                            <List.Item>
                                <GalleryItem $gallery={gallery} />
                            </List.Item>
                        );
                    }}
                />
            </TabPane>
            <TabPane tab="Materiały do pobrania" key="download">
                <List
                    itemLayout="horizontal"
                    dataSource={sortedWhitePapers}
                    rowKey={({ seoSlug }: WhitePaper): string => seoSlug}
                    grid={{ column: 1 }}
                    renderItem={(item: WhitePaper) => (
                        <List.Item>
                            <WhitePaperItem $whitePaper={item} />
                        </List.Item>
                    )}
                />
            </TabPane>
        </TabsStyled>
    );
};

export default RelativeContentTabs;
